import { createSlice } from '@reduxjs/toolkit';
import { message as toaster } from 'antd';
import { API } from 'api';

const initialState = {};

const VENDOR_ID_REQUIRED_ERROR = 'Vendor ID is required';
const LIST_SEARCH_URL = '/list_search';

export const vendorSliceV2 = createSlice({
	name: 'vendorStateV2',
	initialState,
	reducers: {},
});

export const createVendorContractV2 =
	({ req_data, setErrors }) =>
	async () => {
		const data = {
			create_type: 'vendor_contract',
			data: req_data,
		};

		const CREDENTIALS = {
			url: '/create',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			toaster.success('Vendor contract created. Pending approval.');
			return res;
		});
	};

// Helper function to clean and transform filter_by object
const cleanAndTransformFilters = (filter_by) => {
	const cleanFilterBy = {};
	if (!filter_by) return cleanFilterBy;

	// First, collect query_str if it exists, or convert vendor_name/search_term to query_str
	let queryStr = null;
	Object.keys(filter_by).forEach((key) => {
		const value = filter_by[key];
		if (value !== undefined && value !== null && value !== '') {
			// Convert search_term or vendor_name to query_str for API
			// query_str is a special parameter for general search across multiple fields
			if (key === 'query_str') {
				queryStr = value;
			} else if (key === 'search_term' || key === 'vendor_name') {
				// Only set query_str if it's not already set
				if (queryStr === null) {
					queryStr = value;
				}
			} else {
				cleanFilterBy[key] = value;
			}
		}
	});

	// Add query_str if we have one
	if (queryStr !== null) {
		cleanFilterBy.query_str = queryStr;
	}

	return cleanFilterBy;
};

export const getVendorContractListV2 =
	({ filter_by = {}, pagination = { page: 1, per_page: 20 } }) =>
	async () => {
		const cleanFilterBy = cleanAndTransformFilters(filter_by);

		const requestData = {
			search_type: 'vendor_contract',
			filter_by: cleanFilterBy,
			pagination: {
				page: pagination?.page || 1,
				per_page: pagination?.per_page || 20,
			},
		};

		const CREDENTIALS = {
			url: '/search',
			data: requestData,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			// Handle different response structures
			// V2 API with offset/limit might return: response.data (array) or response.data.contracts
			// Count might be in: response.meta.count or response.data.pagination.total
			const data = response?.data || {};
			const rows = Array.isArray(data) ? data : data.contracts || [];
			const count =
				response?.meta?.count ||
				data.pagination?.total ||
				data.pagination?.total_count ||
				data.count ||
				rows.length ||
				0;

			return {
				rows,
				pagination: data.pagination || {},
				count,
			};
		});
	};

/**
 * Update Vendor Contract V2
 * @param {Object} params
 * @param {Object} params.req_data - Vendor contract data to update (must include id field)
 * @param {boolean} [params.edit_flow] - If true, submits an edit-flow payload (id + changes + comments)
 *   for approval instead of a direct update.
 * @param {string} [params.comments] - Required when edit_flow=true. Reason for the edit.
 * @param {Object} [params.changes] - Required when edit_flow=true. Only the fields being changed.
 * @param {Function} params.setErrors - Form error setter function (optional)
 * @returns {Promise} API response with updated vendor contract data
 */
export const updateVendorContractV2 =
	({ req_data, edit_flow = false, comments, changes, setErrors }) =>
	async () => {
		if (
			!req_data ||
			req_data.id === undefined ||
			req_data.id === null ||
			String(req_data.id).trim() === ''
		) {
			toaster.error('Vendor contract ID is required for update');
			return Promise.reject(new Error('Vendor contract ID is required'));
		}

		const dataBody = edit_flow
			? {
					id: req_data.id,
					edit_flow: true,
					comments,
					changes,
			  }
			: req_data;

		const data = {
			update_type: 'vendor_contract',
			data: dataBody,
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res.status === 'success') {
				const defaultMsg = edit_flow
					? 'Edit submitted for approval'
					: 'Vendor contract updated successfully';
				toaster.success(res.message || defaultMsg);
			}
			return res;
		});
	};

/**
 * Get Vendor Contract Edit History V2
 * @param {Object} params
 * @param {string|number} params.contract_id - Contract identifier (e.g. "IRPL-73" or numeric id)
 * @param {string} [params.status] - Filter by status ("approved", "pending", "rejected")
 * @param {Object} [params.pagination] - { page, per_page }
 * @returns {Promise} { rows, count } shaped for RCTable
 */
export const getVendorContractEditHistoryV2 =
	({ contract_id, status, pagination = { page: 1, per_page: 20 } }) =>
	async () => {
		if (contract_id === undefined || contract_id === null || contract_id === '') {
			return { rows: [], count: 0 };
		}

		const filter_by = { contract_id };
		if (status) filter_by.status = status;

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'vendor_contract_edit_history',
				filter_by,
				pagination: {
					page: pagination?.page || 1,
					per_page: pagination?.per_page || 20,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			const data = response?.data || {};
			const rows = Array.isArray(data)
				? data
				: data.audits || data.history || data.rows || data.edit_history || [];
			const count =
				response?.meta?.count ||
				data.pagination?.total ||
				data.pagination?.total_count ||
				data.count ||
				rows.length ||
				0;
			return { rows, count };
		});
	};

// Get single vendor contract by ID
export const getVendorContractByIdV2 =
	({ id, vendor_id }) =>
	async () => {
		// Build filter_by - prioritize vendor_id if provided, otherwise use id
		const filterBy = {};
		if (vendor_id) {
			filterBy.vendor_id = vendor_id;
			// Also include id if available to ensure we get the specific contract
			if (id) {
				filterBy.id = id;
			}
		} else if (id) {
			filterBy.id = id;
		}

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'vendor_contract',
				filter_by: filterBy,
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			const contracts = response?.data?.contracts || [];
			return contracts.length > 0 ? contracts[0] : null;
		});
	};

// Search vendor contract filters (for dropdowns)
export const searchVendorContractFiltersV2 =
	({ filter_by = {}, limit = 50 }) =>
	async () => {
		// Build filter_by object, removing undefined/null values
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					cleanFilterBy[key] = filter_by[key];
				}
			});
		}

		const CREDENTIALS = {
			url: LIST_SEARCH_URL,
			data: {
				list_search_type: 'vendor_contract',
				filter_by: cleanFilterBy,
				limit: limit || 50,
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => ({
			results: response?.data?.results || [],
			total: response?.data?.total || 0,
		}));
	};

/**
 * Search Vehicle Types V2 (for filter dropdowns)
 * @param {Object} filter_by - Filter criteria
 * @param {Number} limit - Maximum number of results (default: 100)
 * @returns {Promise} API response with vehicle types list
 */
export const searchVehicleTypesV2 =
	({ filter_by = {}, limit = 100 }) =>
	async () => {
		// Build filter_by object, removing undefined/null/empty values
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					cleanFilterBy[key] = filter_by[key];
				}
			});
		}

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'vehicle_type',
				filter_by: cleanFilterBy,
				pagination: {
					page: 1,
					per_page: limit || 100,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			// Handle different possible response structures for v2 API
			const data = response?.data || {};
			let vehicleTypes = [];

			// Check if data is an array directly
			if (Array.isArray(data)) {
				vehicleTypes = data;
			} else {
				// Try different possible keys
				vehicleTypes =
					data.vehicles || data.rows || data.results || data.vehicle_types || [];
			}

			return {
				rows: vehicleTypes,
				count: data.pagination?.total || data.count || vehicleTypes.length,
			};
		});
	};

/**
 * Create Vendor V2
 * @param {Object} req_data - Vendor data from all steps
 * @param {Function} setErrors - Error handler function
 * @returns {Promise} API response
 */
export const createVendorV2 =
	({ req_data, setErrors }) =>
	async () => {
		const data = {
			create_type: 'vendor',
			data: req_data,
		};

		const CREDENTIALS = {
			url: '/create',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res.status === 'success') {
				toaster.success(res.message || 'Vendor created successfully');
			}
			return res;
		});
	};

/**
 * Update Vendor V2
 * @param {Object} req_data - Vendor update data (must include id)
 * @param {Function} setErrors - Error handler function
 * @returns {Promise} API response
 */
export const updateVendorV2 =
	({ req_data, setErrors }) =>
	async () => {
		const data = {
			update_type: 'vendor',
			data: req_data,
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res.status === 'success') {
				toaster.success(res.message || 'Vendor updated successfully');
			}
			return res;
		});
	};

/**
 * Search Vendors V2
 * @param {Object} filter_by - Filter criteria
 * @param {Object} pagination - Pagination config { page, per_page }
 * @param {Object} sort_by - Sort config { field, order }
 * @returns {Promise} API response with vendors array and pagination
 */
export const searchVendorV2 =
	({ filter_by = {}, pagination = { page: 1, per_page: 20 }, sort_by = null }) =>
	async () => {
		// Use the same transformation function as vendor contracts
		const cleanFilterBy = cleanAndTransformFilters(filter_by);

		const requestData = {
			search_type: 'vendor',
			filter_by: cleanFilterBy,
			pagination: {
				page: pagination?.page || 1,
				per_page: pagination?.per_page || 20,
			},
		};

		// Add sort_by if provided
		if (sort_by && sort_by.field && sort_by.order) {
			requestData.sort_by = {
				field: sort_by.field,
				order: sort_by.order,
			};
		}

		const CREDENTIALS = {
			url: '/search',
			data: requestData,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			// API returns { status, data: [...], pagination: {...} }
			const vendors = Array.isArray(response?.data) ? response.data : [];
			const paginationData = response?.pagination || {};

			return {
				rows: vendors,
				pagination: paginationData,
				count: paginationData.total || vendors.length || 0,
			};
		});
	};

/**
 * Get Vendor by ID V2
 * @param {Number} id - Vendor ID
 * @returns {Promise} Vendor object or null
 */
export const getVendorByIdV2 =
	({ id }) =>
	async () => {
		// Clean and transform filters for consistency with other search functions
		const cleanFilterBy = cleanAndTransformFilters({
			id,
		});

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'vendor',
				filter_by: cleanFilterBy,
				pagination: {
					page: 1,
					per_page: 1, // Only need one result when filtering by ID
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			// API returns { status, data: [...], pagination: {...} }
			const vendors = Array.isArray(response?.data) ? response.data : [];
			// Find the vendor with the matching ID (in case API returns multiple vendors)
			const vendorId = id;
			const matchingVendor = vendors.find((vendor) => vendor?.id === vendorId);

			// STRICT: Only return vendor if ID matches exactly
			// Do NOT fallback to first vendor - this causes wrong vendor to be loaded
			if (!matchingVendor) {
				console.error('❌ [getVendorByIdV2] Vendor not found or ID mismatch', {
					requestedId: vendorId,
					receivedVendors: vendors.map((v) => ({ id: v?.id, name: v?.vendor_name })),
				});
				return null;
			}

			// Verify the ID matches (double-check)
			if (matchingVendor.id !== vendorId) {
				console.error('❌ [getVendorByIdV2] Vendor ID mismatch', {
					requestedId: vendorId,
					receivedId: matchingVendor.id,
					vendorName: matchingVendor.vendor_name,
				});
				return null;
			}

			return matchingVendor;
		});
	};

/**
 * Search Vendor Filters V2 (for dropdowns/autocomplete)
 * @param {Object} filter_by - Filter criteria
 * @param {Number} limit - Maximum number of results (default: 50)
 * @returns {Promise} API response with results array
 */
export const searchVendorFiltersV2 =
	({ filter_by = {}, limit = 50 }) =>
	async () => {
		// Build filter_by object, removing undefined/null/empty values
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					cleanFilterBy[key] = filter_by[key];
				}
			});
		}

		const CREDENTIALS = {
			url: LIST_SEARCH_URL,
			data: {
				list_search_type: 'vendor',
				filter_by: cleanFilterBy,
				limit: limit || 50,
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			// API may return data as array directly or in data.results
			const results =
				response?.data?.results ?? (Array.isArray(response?.data) ? response.data : []);
			return {
				results,
				total: response?.data?.total ?? response?.total ?? results.length,
			};
		});
	};

/**
 * Search Bank List V2
 * @param {Object} filter_by - Filter criteria
 * @param {Number} limit - Maximum number of results (default: 50)
 * @returns {Promise} API response
 */
export const searchBankListV2 =
	({ filter_by = {}, limit = 50 }) =>
	async () => {
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					cleanFilterBy[key] = filter_by[key];
				}
			});
		}

		const CREDENTIALS = {
			url: LIST_SEARCH_URL,
			data: {
				list_search_type: 'bank',
				filter_by: cleanFilterBy,
				limit: limit || 50,
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => ({
			results: response?.data || [],
			total: response?.total || 0,
		}));
	};

/**
 * Export Vendor CSV V2
 * @param {Object} filter_by - Filter criteria for export
 * @param {String} format - Export format (default: 'csv')
 * @returns {Promise} API response with download_url
 */
export const exportVendorCSVV2 =
	({ filter_by = {}, format = 'csv' }) =>
	async () => {
		// Build filter_by object, removing undefined/null/empty values
		const cleanFilterBy = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				if (
					filter_by[key] !== undefined &&
					filter_by[key] !== null &&
					filter_by[key] !== ''
				) {
					cleanFilterBy[key] = filter_by[key];
				}
			});
		}

		const CREDENTIALS = {
			url: '/report',
			data: {
				report_type: 'vendor_export',
				format: format || 'csv',
				data: {
					filter_by: cleanFilterBy,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			if (response?.status === 'success' && response?.data?.download_url) {
				// Open download URL in new tab
				window.open(response.data.download_url, '_blank');
				toaster.success(
					`Export generated successfully. ${
						response.data.total_records || 0
					} records exported.`
				);
			}
			return response;
		});
	};

/**
 * Export Vendor Contract CSV V2
 * Endpoint: POST /api/v2/report
 * @param {Object} filter_by - Filter criteria for export (same as getVendorContractListV2 filters)
 * @param {String} format - Export format (default: 'csv')
 * @returns {Promise} API response with download_url
 */
export const exportVendorContractCSVV2 =
	({ filter_by = {}, format = 'csv' }) =>
	async () => {
		// Build filter_by object, removing undefined/null/empty values
		const cleanFilterBy = cleanAndTransformFilters(filter_by);

		const CREDENTIALS = {
			url: '/report',
			data: {
				report_type: 'vendor_contract_export',
				format: format || 'csv',
				data: {
					filter_by: cleanFilterBy,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			if (response?.status === 'success' && response?.data?.download_url) {
				// Automatically trigger download
				const link = document.createElement('a');
				link.href = response.data.download_url;
				link.download = response.data.file_name || 'vendor_contract_export.csv';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				toaster.success(
					`Export generated successfully. ${
						response.data.total_records || 0
					} records exported.`
				);
				return response.data;
			}
			// Handle async task response
			if (response?.data?.task_id) {
				toaster.info(
					`Export is being processed (Task ID: ${response.data.task_id}). You will be notified when ready.`
				);
				return response.data;
			}
			// Fallback
			toaster.warning('Export response format unexpected. Please contact support.');
			return response?.data || {};
		});
	};

/**
 * Get Vendor Registration Form V2
 * @param {Number} vendor_id - Vendor ID
 * @returns {Promise} API response with download_url
 */
export const getVendorRegistrationFormV2 =
	({ vendor_id }) =>
	async () => {
		if (!vendor_id) {
			throw new Error(VENDOR_ID_REQUIRED_ERROR);
		}

		const CREDENTIALS = {
			url: '/report',
			data: {
				report_type: 'vendor_registration_form',
				format: 'file',
				data: {
					vendor_id,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			if (response?.status === 'success' && response?.data?.download_url) {
				// Open download URL in new tab
				window.open(response.data.download_url, '_blank');
				toaster.success('Registration form downloaded successfully');
			}
			return response;
		});
	};

/**
 * Update Vendor Bank Details V2
 * @param {Number} vendor_id - Vendor ID
 * @param {Object} banking_info - Banking information object
 * @param {Function} setErrors - Error handler function
 * @returns {Promise} API response
 */
export const updateVendorBankDetailsV2 =
	({ vendor_id, banking_info, setErrors }) =>
	async () => {
		if (!vendor_id) {
			throw new Error(VENDOR_ID_REQUIRED_ERROR);
		}

		if (!banking_info) {
			throw new Error('Banking information is required');
		}

		const data = {
			update_type: 'vendor_bank_details',
			data: {
				vendor_id,
				banking_info,
			},
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res.status === 'success') {
				toaster.success(
					res.message || 'Bank details submitted successfully. Awaiting approval.'
				);
			}
			return res;
		});
	};

/**
 * Add Vendor Servicability Route V2
 * @param {Number} vendor_id - Vendor ID
 * @param {String} type_of_movement - Type of movement (Intracity/Intercity)
 * @param {String} source_city - Source city
 * @param {String} destination_city - Destination city
 * @param {Function} setErrors - Error handler function
 * @returns {Promise} API response
 */
export const addVendorServicabilityRouteV2 =
	({ vendor_id, type_of_movement, source_city, destination_city, setErrors }) =>
	async () => {
		if (!vendor_id) {
			throw new Error(VENDOR_ID_REQUIRED_ERROR);
		}

		if (!type_of_movement || !source_city || !destination_city) {
			throw new Error('Type of movement, source city, and destination city are required');
		}

		const data = {
			create_type: 'vendor_servicability_route',
			data: {
				vendor_id,
				type_of_movement,
				source_city,
				destination_city,
			},
		};

		const CREDENTIALS = {
			url: '/create',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res.status === 'success') {
				toaster.success(res.message || 'Vendor servicability route added successfully');
			}
			return res;
		});
	};

/**
 * Update Vendor Servicability Route V2
 * @param {Number} id - Route ID
 * @param {String} type_of_movement - Type of movement (Intracity/Intercity)
 * @param {String} source_city - Source city
 * @param {String} destination_city - Destination city
 * @param {Boolean} is_active - Whether route is active
 * @param {Function} setErrors - Error handler function
 * @returns {Promise} API response
 */
export const updateVendorServicabilityRouteV2 =
	({ id, type_of_movement, source_city, destination_city, is_active, setErrors }) =>
	async () => {
		if (!id) {
			throw new Error('Route ID is required');
		}

		const data = {
			update_type: 'vendor_servicability_route',
			data: {
				id,
				...(type_of_movement && { type_of_movement }),
				...(source_city && { source_city }),
				...(destination_city && { destination_city }),
				...(typeof is_active === 'boolean' && { is_active }),
			},
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res.status === 'success') {
				toaster.success(res.message || 'Vendor servicability route updated successfully');
			}
			return res;
		});
	};

/**
 * Remove Vendor Servicability Route V2 (soft delete by setting is_active to false)
 * @param {Number} id - Route ID
 * @param {Function} setErrors - Error handler function
 * @returns {Promise} API response
 */
export const removeVendorServicabilityRouteV2 =
	({ id, setErrors }) =>
	async () => {
		if (!id) {
			throw new Error('Route ID is required');
		}

		const data = {
			update_type: 'vendor_servicability_route',
			data: {
				id,
				is_active: false,
			},
		};

		const CREDENTIALS = {
			url: '/update',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((res) => {
			if (res.status === 'success') {
				toaster.success(res.message || 'Vendor servicability route removed successfully');
			}
			return res;
		});
	};

// Helper function to calculate pagination offset and limit
const calculatePagination = (pagination) => {
	// Support both { offset, limit } and { page, per_page } pagination formats
	if (pagination?.page && pagination?.per_page) {
		const page = pagination.page || 1;
		const perPage = pagination.per_page || 100;
		return {
			offset: (page - 1) * perPage,
			limit: perPage,
		};
	}

	// Default to offset/limit format
	return {
		offset: pagination?.offset ?? 0,
		limit: pagination?.limit ?? 100,
	};
};

/**
 * Search Routes V2
 * @param {Object} filter_by - Filter criteria
 * @param {Object} pagination - Pagination config { page, per_page } or { offset, limit }
 * @returns {Promise} API response with routes array and pagination
 */
export const searchRoutesV2 =
	({ filter_by = {}, pagination = {}, limit, offset }) =>
	async () => {
		// Build filter_by object, removing undefined/null/empty values
		const cleanFilterBy = cleanAndTransformFilters(filter_by);

		// Calculate pagination - allow top-level limit/offset to override pagination object
		const paginationWithOverrides = {
			offset: offset ?? pagination.offset ?? 0,
			limit: limit ?? pagination.limit ?? 100,
		};
		const calculatedPagination = calculatePagination(paginationWithOverrides);

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'route',
				filter_by: cleanFilterBy,
				offset: calculatedPagination.offset,
				limit: calculatedPagination.limit,
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			// Handle different response structures
			const data = response?.data || {};
			let routes = [];

			// Check if data is an array directly
			if (Array.isArray(data)) {
				routes = data;
			} else {
				// Try different possible keys for routes
				routes = data.routes || data.rows || data.results || data.data || [];
			}

			return {
				rows: routes,
				count: data.pagination?.total || data.count || data.total || routes.length || 0,
			};
		});
	};

/**
 * Prefill Per-Order rates for a vendor contract from the matched client contract.
 *
 * @param {Object} params
 * @param {number} params.project_id - required
 * @param {number} params.vehicle_type_id - required
 * @param {number|null} params.route_id - nullable (null = route-agnostic)
 * @param {string} params.contract_date - required 'YYYY-MM-DD' (vendor contract start)
 * @param {string} params.expiry_date - required 'YYYY-MM-DD' (vendor contract end)
 * @param {string} params.billing_type - required (e.g. 'Fixed + Variable')
 * @param {string} params.billing_subtype - required ('Per Order' | 'Per Kilometer')
 * @returns {Promise<{orderRates: Array<{rate_id: string, client_rate: number}>, fixedRatePerDay: number|null, error: string|null}>}
 */
export const getClientContractPerOrderRatesV2 =
	({
		project_id,
		vehicle_type_id,
		route_id,
		contract_date,
		expiry_date,
		billing_type,
		billing_subtype,
	}) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'client_contract_order_rates_for_vendor',
				filter_by: {
					project_id,
					vehicle_type_id,
					route_id: route_id ?? null,
					contract_date,
					expiry_date,
					billing_type,
					billing_subtype,
				},
			},
			version: 'v2',
		};

		return API.common(CREDENTIALS)
			.then((response) => {
				const orderRates = response?.data?.order_rates;
				return {
					orderRates: Array.isArray(orderRates) ? orderRates : [],
					fixedRatePerDay: response?.data?.fixed_rate_per_day ?? null,
					error: null,
				};
			})
			.catch((err) => ({
				orderRates: [],
				fixedRatePerDay: null,
				error:
					err?.response?.data?.error?.message ||
					'Failed to load per-order rates from client contract',
			}));
	};

/**
 * Search Vendor Servicability Routes V2
 * Used to fetch dynamic source and destination cities
 * @param {Object} params - Search parameters
 * @param {Object} params.filter_by - Filter criteria (vendor_id, type_of_movement, source_city, destination_city, is_active)
 * @param {Object} params.pagination - Pagination options
 * @param {Function} params.setErrors - Error handler function
 * @returns {Promise} API response with routes array and pagination
 */
export const searchVendorServicabilityRoutesV2 =
	({ filter_by = {}, pagination = { page: 1, per_page: 20 }, setErrors }) =>
	async () => {
		const cleanFilterBy = cleanAndTransformFilters(filter_by);

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'vendor_servicability_route',
				filter_by: cleanFilterBy,
				pagination,
			},
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			const data = response?.data || {};
			const routes = data.routes || [];
			const pagination_data = data.pagination || {};

			return {
				routes,
				pagination: pagination_data,
			};
		});
	};

/**
 * Bulk Upload Vehicles V2
 * Uploads multiple vehicles from a CSV file
 * @param {Object} params - Upload parameters
 * @param {string} params.fileKey - S3 key of the uploaded CSV file
 * @param {Function} params.setErrors - Error handler function
 * @returns {Promise} API response with success/error counts
 */
export const bulkUploadVehiclesV2 =
	({ fileKey, setErrors }) =>
	async () => {
		const data = {
			upload_type: 'vehicle',
			data: {
				key: fileKey,
			},
		};

		const CREDENTIALS = {
			url: '/bulk-upload',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			if (response?.status === 'success') {
				const { success_count, error_count, total_count } = response.data || {};
				toaster.success(
					`Bulk upload completed: ${success_count} success, ${error_count} errors out of ${total_count} total`
				);
			}
			return response;
		});
	};

export const bulkUploadServicabilityRoutesV2 =
	({ fileKey, entity, setErrors }) =>
	async () => {
		const data = {
			upload_type: 'route',
			data: {
				...(entity && { entity }),
				key: fileKey,
			},
		};

		const CREDENTIALS = {
			url: '/bulk-upload',
			data,
			setErrors,
			version: 'v2',
		};

		return API.common(CREDENTIALS).then((response) => {
			if (response?.status === 'success') {
				const { success_count, error_count, total_count, download_url } =
					response.data || {};
				toaster.success(
					`Bulk upload completed: ${success_count} success, ${error_count} errors out of ${total_count} total.`
				);
				return { ...response, download_url };
			}
			toaster.error('Bulk upload failed.');
			return response;
		});
	};

export default vendorSliceV2.reducer;
